import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { money, useCountdown, pad2 } from "./lib";
import { Sheet } from "./ui";
import {
  AUD_META,
  depositPct,
  endingSoon,
  balanceNote,
  fcTheme,
  isBalanceBlocked,
  isScoped,
  playChipText,
  quotaLow,
  requirementText,
  useFreeCredits,
} from "./freeCreditLogic";

/*
 * ส่วน "เครดิตฟรี" บนหน้าแรก (วางเหนือผลหวยรัฐบาลไทย) — ดีไซน์แบบ "ของขวัญเด่น + ภารกิจ"
 *   • แคมเปญแรก (พร้อมรับก่อน) เป็นการ์ดใหญ่ไล่สีพร้อมของขวัญลอย
 *   • ที่เหลือเป็นรายการภารกิจ: วงแหวนแสดงความคืบหน้า (ยอดฝาก / สิทธิ์ที่เหลือ) + ปุ่มกดรับ
 *   • กดรับสำเร็จ = หน้าต่างเปิดกล่องของขวัญ ฝนเหรียญ ตัวเลขนับขึ้น
 * ตรรกะ (โหลด/รีเฟรช/กดรับ) อยู่ freeCreditLogic.js — ใช้ร่วมกับ landing
 */

const ART = {
  NEW_USER: "fluent-emoji-flat:wrapped-gift",
  DEPOSIT: "fluent-emoji-flat:money-bag",
  ALL: "fluent-emoji-flat:confetti-ball",
};

const COINS = Array.from({ length: 16 }, (_, i) => ({
  left: (i * 41 + 5) % 96,
  delay: (i % 8) * 0.11,
  dur: 1.6 + (i % 5) * 0.28,
  size: 20 + (i % 3) * 6,
}));

function useCountUp(target, ms = 900) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / ms);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

function EndClock({ at }) {
  const c = useCountdown(at);
  if (c.done) return null;
  return (
    <span className="fcv-end" role="timer">
      <Icon icon="solar:alarm-bold" width={13} />
      หมดเขตใน{" "}
      <b>
        {c.d > 0 ? `${c.d}วัน ` : ""}
        {pad2(c.h)}:{pad2(c.m)}:{pad2(c.s)}
      </b>
    </span>
  );
}

function Ring({ pct, done, icon, size = 54 }) {
  const r = 22;
  const circ = 2 * Math.PI * r;
  const off = circ * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <span className={`fcv-ring${done ? " done" : ""}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 54 54" className="fcv-ring-svg" aria-hidden="true">
        <circle cx="27" cy="27" r={r} className="trk" />
        <circle cx="27" cy="27" r={r} className="val" strokeDasharray={circ} strokeDashoffset={off} />
      </svg>
      <Icon icon={icon} width={26} height={26} />
    </span>
  );
}

// ปุ่มตามสถานะ — compact = ปุ่มเล็กท้ายแถวรายการ
function Action({ c, busy, onClaim, onLogin, onDeposit, compact }) {
  const cls = `fcv-btn${compact ? " sm" : ""}`;
  if (c.state === "CLAIMABLE")
    return (
      <button type="button" className={`${cls} go`} onClick={() => onClaim(c)} disabled={busy}>
        {busy ? <Icon icon="svg-spinners:ring-resize" width={16} /> : <Icon icon="solar:gift-bold" width={16} />}
        {busy ? "กำลังรับ" : "กดรับ"}
      </button>
    );
  if (c.state === "LOGIN")
    return (
      <button type="button" className={`${cls} go`} onClick={onLogin}>
        <Icon icon="solar:login-3-bold" width={16} /> {compact ? "เข้าสู่ระบบ" : "เข้าสู่ระบบเพื่อรับ"}
      </button>
    );
  if (c.state === "CLAIMED")
    return (
      <button type="button" className={`${cls} done`} disabled>
        <Icon icon="solar:check-circle-bold" width={16} /> {c.repeat_mode === "DAILY" ? "รับวันนี้แล้ว" : "รับแล้ว"}
      </button>
    );
  if (c.state === "FULL")
    return (
      <button type="button" className={`${cls} off`} disabled>
        สิทธิ์เต็ม
      </button>
    );
  if (isBalanceBlocked(c))
    return (
      <button type="button" className={`${cls} off`} disabled>
        {compact ? "มียอดคงเหลือ" : "ยังมียอดเงินคงเหลือในกระเป๋า"}
      </button>
    );
  if (c.audience === "DEPOSIT")
    return (
      <button type="button" className={`${cls} dep`} onClick={onDeposit}>
        <Icon icon="solar:wallet-money-bold" width={16} /> ฝากเงิน
      </button>
    );
  return (
    <button type="button" className={`${cls} off`} disabled>
      ยังไม่เข้าเงื่อนไข
    </button>
  );
}

function Hero({ c, busy, onClaim, onInfo, onLogin, onDeposit }) {
  const th = fcTheme(c.theme);
  const race = c.quota > 0;
  const low = quotaLow(c);
  const pctLeft = race ? Math.max(0, Math.min(100, (c.remaining / c.quota) * 100)) : 0;
  const prog = c.progress;
  return (
    <article className={`fcv-hero th-${c.theme} st-${c.state}`} style={{ "--fc-a": th.a, "--fc-b": th.b, "--fc-ink": th.ink }}>
      <span className="fcv-orb o1" aria-hidden="true" />
      <span className="fcv-orb o2" aria-hidden="true" />
      <Icon icon={ART[c.audience] || ART.ALL} className="fcv-hero-art" width={150} height={150} aria-hidden="true" />
      <div className="fcv-hero-top">
        <div className="fcv-pills">
          <span className="fcv-pill">
            <Icon icon={(AUD_META[c.audience] || AUD_META.ALL).icon} width={14} /> {(AUD_META[c.audience] || AUD_META.ALL).label}
          </span>
          {race && (
            <span className="fcv-pill hot">
              <Icon icon="fluent-emoji-flat:fire" width={13} /> แย่งกัน
            </span>
          )}
          {c.repeat_mode === "DAILY" && <span className="fcv-pill">ทุกวัน</span>}
        </div>
        <h3 className="fcv-hero-name">{c.name}</h3>
        {c.description && <p className="fcv-hero-desc">{c.description}</p>}
        <div className="fcv-amt" aria-label={`เครดิตฟรี ${money(c.reward_amount)} บาท`}>
          <small>รับฟรี</small>
          <b>฿{money(c.reward_amount)}</b>
        </div>
      </div>

      <div className="fcv-sheet">
        <ul className="fcv-facts">
          <li>
            <Icon icon="solar:checklist-minimalistic-bold" width={18} /> {requirementText(c)}
          </li>
          <li>
            <Icon icon={c.turnover_multiplier > 0 ? "solar:refresh-circle-bold" : "solar:bolt-circle-bold"} width={18} />
            {c.turnover_multiplier > 0 ? `ทำเทิร์น ${c.turnover_multiplier} เท่าก่อนถอน` : "ไม่ติดเทิร์น ถอนได้ทันที"}
          </li>
          <li title={c.play_label}>
            <Icon icon={isScoped(c) ? "solar:ticket-bold" : "solar:globus-bold"} width={18} /> {playChipText(c)}
          </li>
        </ul>

        {race && c.state !== "FULL" && (
          <div className="fcv-meter">
            <div>
              <span>
                {low && <Icon icon="fluent-emoji-flat:fire" width={14} />} เหลือ <b>{money(c.remaining)}</b>/{money(c.quota)} สิทธิ์
                {c.repeat_mode === "DAILY" ? " วันนี้" : ""}
              </span>
              {low && <em>ใกล้หมด!</em>}
            </div>
            <i>
              <u style={{ width: `${pctLeft}%` }} />
            </i>
          </div>
        )}
        {prog?.kind === "deposit" && c.state === "NOT_ELIGIBLE" && (
          <div className="fcv-meter">
            <div>
              <span>
                ฝากแล้ว <b>฿{money(prog.have)}</b> / ฿{money(prog.need)}
              </span>
              <em>อีก ฿{money(Math.max(0, prog.need - prog.have))}</em>
            </div>
            <i>
              <u style={{ width: `${depositPct(c)}%` }} />
            </i>
          </div>
        )}
        {balanceNote(c) && (
          <div className={`fcv-bnote${isBalanceBlocked(c) ? " warn" : ""}`}>
            <Icon icon="solar:info-circle-bold" width={15} />
            <span>
              {balanceNote(c)}
              {isBalanceBlocked(c) && c.reason ? ` — ${c.reason}` : ""}
            </span>
          </div>
        )}
        <Action c={c} busy={busy} onClaim={onClaim} onLogin={onLogin} onDeposit={onDeposit} />
        <div className="fcv-hero-foot">
          {endingSoon(c) && c.state !== "CLAIMED" && c.state !== "FULL" && <EndClock at={c.end_at} />}
          <button type="button" className="fcv-link" onClick={() => onInfo(c)}>
            <Icon icon="solar:info-circle-linear" width={14} /> รายละเอียดและเงื่อนไข
          </button>
        </div>
      </div>
    </article>
  );
}

function Row({ c, busy, onClaim, onInfo, onLogin, onDeposit }) {
  const th = fcTheme(c.theme);
  const race = c.quota > 0 && c.state !== "FULL";
  const prog = c.progress;
  const isDep = prog?.kind === "deposit" && c.state === "NOT_ELIGIBLE";
  const pct = c.state === "CLAIMED" ? 100 : isDep ? depositPct(c) : race ? (c.remaining / c.quota) * 100 : 100;
  const icon = c.state === "CLAIMED" ? "solar:check-circle-bold" : ART[c.audience] || ART.ALL;
  return (
    <li className={`fcv-row st-${c.state}`} style={{ "--fc-a": th.a, "--fc-b": th.b }}>
      <Ring pct={pct} done={c.state === "CLAIMED"} icon={icon} />
      <div className="fcv-row-main">
        <b>{c.name}</b>
        <span>{requirementText(c)}</span>
        <span className="fcv-scope">
          <Icon icon={isScoped(c) ? "solar:ticket-bold" : "solar:globus-bold"} width={12} /> {playChipText(c)}
        </span>
        {isDep && (
          <em>
            ฝากแล้ว ฿{money(prog.have)} / ฿{money(prog.need)}
          </em>
        )}
        {race && (
          <em className={quotaLow(c) ? "low" : ""}>
            {quotaLow(c) && "🔥 "}เหลือ {money(c.remaining)}/{money(c.quota)} สิทธิ์
          </em>
        )}
        {balanceNote(c) && <span className={`fcv-bnote-sm${isBalanceBlocked(c) ? " warn" : ""}`}>{balanceNote(c)}</span>}
        {c.state === "CLAIMABLE" && endingSoon(c) && <EndClock at={c.end_at} />}
        <button type="button" className="fcv-link" onClick={() => onInfo(c)}>
          เงื่อนไข
        </button>
      </div>
      <div className="fcv-row-end">
        <span className="amt">฿{money(c.reward_amount)}</span>
        <Action c={c} busy={busy} onClaim={onClaim} onLogin={onLogin} onDeposit={onDeposit} compact />
      </div>
    </li>
  );
}

function Opened({ r, onClose, onPlay }) {
  const th = fcTheme(r?.theme);
  const n = useCountUp(r?.amount || 0);
  const ref = useRef(null);
  useEffect(() => {
    if (!r) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    ref.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [r, onClose]);
  if (!r) return null;
  return (
    <div className="fcv-pop-wrap" onClick={onClose}>
      <div
        className="fcv-pop"
        role="dialog"
        aria-modal="true"
        aria-label="รับเครดิตฟรีสำเร็จ"
        onClick={(e) => e.stopPropagation()}
        style={{ "--fc-a": th.a, "--fc-b": th.b }}
      >
        <div className="fcv-rays" aria-hidden="true" />
        <div className="fcv-coins" aria-hidden="true">
          {COINS.map((p, i) => (
            <Icon
              key={i}
              icon="fluent-emoji-flat:coin"
              width={p.size}
              height={p.size}
              style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s` }}
            />
          ))}
        </div>
        <Icon icon="fluent-emoji-flat:wrapped-gift" className="fcv-pop-gift" width={104} height={104} aria-hidden="true" />
        <div className="fcv-pop-kick">เปิดของขวัญสำเร็จ!</div>
        <div className="fcv-pop-amt">฿{money(Math.round(n * 100) / 100)}</div>
        <div className="fcv-pop-name">จาก “{r.name}”</div>
        {r.balance_after != null && <div className="fcv-pop-bal">ยอดเงินคงเหลือ ฿{money(r.balance_after)}</div>}
        {r.play_label && (
          <div className="fcv-pop-turn">
            <Icon icon="solar:ticket-bold" width={15} /> เครดิตฟรีนี้ใช้แทงได้{r.play_label}
          </div>
        )}
        {r.turnover_required > 0 && (
          <div className="fcv-pop-turn">
            <Icon icon="solar:refresh-circle-bold" width={15} /> ทำเทิร์นครบ ฿{money(r.turnover_required)} จึงถอนส่วนนี้ได้
          </div>
        )}
        <button type="button" className="fcv-btn go big" ref={ref} onClick={onPlay}>
          <Icon icon="solar:ticket-bold" width={18} /> ไปแทงหวยกันเลย
        </button>
        <button type="button" className="fcv-link" onClick={onClose}>
          ปิด
        </button>
      </div>
    </div>
  );
}

export default function FreeCreditSection({ onClaimed }) {
  const nav = useNavigate();
  const { items, loading, claimingId, claim, result, closeResult } = useFreeCredits(onClaimed);
  const [info, setInfo] = useState(null);
  const ready = useMemo(() => (items || []).filter((c) => c.state === "CLAIMABLE").length, [items]);

  if (!loading && !items?.length) return null;

  const common = { onClaim: claim, onInfo: setInfo, onLogin: () => nav("/auth"), onDeposit: () => nav("/deposit") };
  const [first, ...rest] = items || [];

  return (
    <section className="vx-block fcv" aria-label="เครดิตฟรี">
      <div className="fcv-head">
        <span className="fcv-kick">
          <Icon icon="fluent-emoji-flat:wrapped-gift" width={18} /> เครดิตฟรี
        </span>
        <h2 className="fcv-title">ของขวัญรอคุณอยู่</h2>
        <p className="fcv-sub">แจกจริง กดรับเอง เครดิตเข้ากระเป๋าทันที</p>
        {ready > 0 && (
          <span className="fcv-ready">
            <b>{ready}</b> พร้อมรับ
          </span>
        )}
      </div>

      {loading ? (
        <div className="fcv-grid">
          <div className="fcv-hero sk" />
          <div className="fcv-list">
            <div className="fcv-row sk" />
            <div className="fcv-row sk" />
          </div>
        </div>
      ) : (
        <div className={`fcv-grid${rest.length ? " two" : ""}`}>
          <Hero c={first} busy={claimingId === first.id} {...common} />
          {!!rest.length && (
            <ul className="fcv-list">
              {rest.map((c) => (
                <Row key={c.id} c={c} busy={claimingId === c.id} {...common} />
              ))}
            </ul>
          )}
        </div>
      )}

      <Sheet open={!!info} onClose={() => setInfo(null)} title={info?.name}>
        {info && (
          <div className="fcv-terms">
            <div className="fcv-terms-amt">
              เครดิตฟรี <b>฿{money(info.reward_amount)}</b>
            </div>
            <ul>
              <li>
                <Icon icon="solar:users-group-rounded-bold-duotone" width={17} /> ผู้ที่รับได้: {info.audience_label}
              </li>
              <li>
                <Icon icon="solar:ticket-bold" width={17} /> ใช้แทงได้: {isScoped(info) ? (info.play_names || []).join(", ") : "ทุกหวย"}
              </li>
              {balanceNote(info) && (
                <li>
                  <Icon icon="solar:wallet-bold" width={17} /> {balanceNote(info).replace(/^\*\s*/, "")}
                </li>
              )}
              <li>
                <Icon icon="solar:repeat-bold" width={17} /> {info.repeat_mode === "DAILY" ? "รับได้วันละ 1 ครั้ง" : "รับได้คนละ 1 ครั้ง"}
              </li>
              <li>
                <Icon icon="solar:refresh-circle-bold" width={17} />
                {info.turnover_multiplier > 0
                  ? `ต้องทำเทิร์นโอเวอร์ ${info.turnover_multiplier} เท่า (฿${money(info.reward_amount * info.turnover_multiplier)}) ก่อนถอน`
                  : "ไม่ติดเทิร์น ถอนได้ทันที"}
              </li>
              {info.quota > 0 && (
                <li>
                  <Icon icon="fluent-emoji-flat:fire" width={17} /> แย่งกันกด จำกัด {money(info.quota)} สิทธิ์
                  {info.repeat_mode === "DAILY" ? "ต่อวัน" : ""} (เหลือ {money(info.remaining)})
                </li>
              )}
              {info.end_at && (
                <li>
                  <Icon icon="solar:calendar-bold" width={17} /> หมดเขต{" "}
                  {new Date(info.end_at).toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" })}
                </li>
              )}
            </ul>
            {info.terms && <p className="fcv-terms-note">{info.terms}</p>}
          </div>
        )}
      </Sheet>

      <Opened
        r={result}
        onClose={closeResult}
        onPlay={() => {
          closeResult();
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />
    </section>
  );
}
